// Project 8: 발행 대상 플랫폼 선택 컴포넌트
import { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { publishService } from '@/services/PublishService';
import type { PublishTarget, PublishPlatform, PublishRequest } from '@/types/publish';
import { TokenModal } from './TokenModal';

interface PublishingTargetsProps {
  contentId: number;
  onPublishComplete?: (results: Record<string, any>) => void;
  onTokenModalOpen?: (platform: PublishPlatform) => void;
}

export function PublishingTargets({ contentId, onPublishComplete, onTokenModalOpen }: PublishingTargetsProps) {
  const [selected, setSelected] = useState<PublishPlatform[]>([]);
  const [showTokenModal, setShowTokenModal] = useState(false);
  const [tokenModalPlatform, setTokenModalPlatform] = useState<PublishPlatform | null>(null);

  // 플랫폼 목록 조회
  const { data: targetsData, refetch: refetchTargets } = useQuery({
    queryKey: ['publish-targets'],
    queryFn: () => publishService.getTargets(),
  });

  // 발행 요청
  const publishMutation = useMutation({
    mutationFn: (request: PublishRequest) => publishService.publish(request),
    onSuccess: (data) => {
      if (data.success && onPublishComplete) {
        onPublishComplete(data.data?.results || {});
      }
      // 플랫폼 목록 다시 조회 (토큰 상태 업데이트)
      refetchTargets();
    },
  });

  const targets = targetsData?.data?.targets || [];

  const toggleSelection = (platform: PublishPlatform) => {
    setSelected((prev) => {
      if (prev.includes(platform)) {
        return prev.filter((p) => p !== platform);
      } else {
        return [...prev, platform];
      }
    });
  };

  const handlePublish = () => {
    if (selected.length === 0) {
      alert('발행할 플랫폼을 선택해주세요.');
      return;
    }

    publishMutation.mutate({
      contentId,
      platforms: selected,
    });
  };

  const getStatusBadge = (target: PublishTarget) => {
    if (!target.connected) {
      return { text: '연결 필요', color: '#999', bg: '#f0f0f0' };
    }
    if (target.is_expired) {
      return { text: '만료됨', color: '#d63031', bg: '#fee' };
    }
    return { text: '연결됨', color: '#00b894', bg: '#d4edda' };
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <h3 style={{ marginTop: 0, fontSize: '1.125rem', fontWeight: 600 }}>
        발행할 플랫폼 선택
      </h3>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '1rem'
      }}>
        {targets.map((target) => {
          const isSelected = selected.includes(target.platform);
          const statusBadge = getStatusBadge(target);

          return (
            <button
              key={target.platform}
              onClick={() => {
                if (target.connected && !target.is_expired) {
                  toggleSelection(target.platform);
                } else {
                  // 토큰 모달 열기
                  if (onTokenModalOpen) {
                    onTokenModalOpen(target.platform);
                  } else {
                    setTokenModalPlatform(target.platform);
                    setShowTokenModal(true);
                  }
                }
              }}
              disabled={!target.connected || target.is_expired}
              style={{
                padding: '1rem',
                border: `2px solid ${isSelected ? '#2196f3' : '#ddd'}`,
                borderRadius: 8,
                background: isSelected ? '#e3f2fd' : '#fff',
                cursor: target.connected && !target.is_expired ? 'pointer' : 'not-allowed',
                display: 'flex',
                flexDirection: 'column',
                gap: '0.5rem',
                textAlign: 'left',
                opacity: target.connected && !target.is_expired ? 1 : 0.6
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span style={{ fontSize: '1.5rem' }}>{target.icon}</span>
                <span style={{ fontSize: '1rem', fontWeight: 600 }}>{target.label}</span>
              </div>

              <div style={{
                fontSize: '0.875rem',
                color: statusBadge.color,
                background: statusBadge.bg,
                padding: '0.25rem 0.5rem',
                borderRadius: 4,
                display: 'inline-block',
                width: 'fit-content'
              }}>
                {statusBadge.text}
              </div>

              {target.display_name && (
                <div style={{ fontSize: '0.75rem', color: '#666' }}>
                  {target.display_name}
                </div>
              )}

              {target.expires_at && (
                <div style={{ fontSize: '0.75rem', color: '#999' }}>
                  만료: {new Date(target.expires_at).toLocaleDateString('ko-KR')}
                </div>
              )}

              {isSelected && (
                <div style={{
                  marginTop: '0.5rem',
                  padding: '0.25rem',
                  background: '#2196f3',
                  color: '#fff',
                  borderRadius: 4,
                  fontSize: '0.75rem',
                  textAlign: 'center'
                }}>
                  선택됨 ✓
                </div>
              )}
            </button>
          );
        })}
      </div>

      <button
        onClick={handlePublish}
        disabled={selected.length === 0 || publishMutation.isPending}
        style={{
          width: '100%',
          padding: '0.75rem',
          background: selected.length > 0 && !publishMutation.isPending ? '#2196f3' : '#ccc',
          color: '#fff',
          border: 'none',
          borderRadius: 8,
          cursor: selected.length > 0 && !publishMutation.isPending ? 'pointer' : 'not-allowed',
          fontSize: '1rem',
          fontWeight: 600
        }}
      >
        {publishMutation.isPending ? '발행 중...' : `선택한 ${selected.length}개 플랫폼으로 발행`}
      </button>

      {publishMutation.isError && (
        <div style={{
          padding: '0.75rem',
          background: '#fee',
          border: '1px solid #f00',
          borderRadius: 8,
          color: '#c00',
          fontSize: '0.875rem'
        }}>
          발행 실패: {publishMutation.error?.message || '알 수 없는 오류'}
        </div>
      )}

      {publishMutation.isSuccess && publishMutation.data?.success && (
        <div style={{
          padding: '0.75rem',
          background: '#d4edda',
          border: '1px solid #00b894',
          borderRadius: 8,
          color: '#155724',
          fontSize: '0.875rem'
        }}>
          발행이 완료되었습니다!
        </div>
      )}

      {/* 토큰 관리 모달 */}
      <TokenModal
        platform={tokenModalPlatform || 'wordpress'}
        isOpen={showTokenModal}
        onClose={() => {
          setShowTokenModal(false);
          setTokenModalPlatform(null);
        }}
        onSuccess={() => {
          refetchTargets();
        }}
      />
    </div>
  );
}

